import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  FolderOpen as FolderOpenIcon,
  CloudUpload as CloudUploadIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  PersonAdd as PersonAddIcon,
  Description as DescriptionIcon,
  Launch as LaunchIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useAuth, UserAssignment } from '../contexts/AuthContext';
import { customerService, Customer, ClientFolderStatus, TaxReturnStatus } from '../services/customerService';
import { invoiceService } from '../services/invoiceService';
import clientFolderService, { type ClientFolder } from '../services/clientFolderService';
import boxService, { type BoxFile } from '../services/boxService';
import { authService } from '../services/authService';
import { engagementService, CustomerEngagementLetter } from '../services/engagementService';
import taxReturnTrackerService, {
  type TaxReturnTrackerResult,
} from '../services/taxReturnTrackerService';
import taxReturnNotesService, {
  type TaxReturnNote,
} from '../services/taxReturnNotesService';
import TaxReturnStatusTracker from './TaxReturnStatusTracker';
import TermsConditionsModal from './TermsConditionsModal';
import type { UserAccessLevel } from '../services/authService';
import { getBrandLogoAlt, getBrandLogoSrc, getLoginPath } from '../utils/branding';

interface LineItem {
  name: string;
  quantity: string;
  base_price_money?: {
    amount: number;
    currency: string;
  };
  total_money?: {
    amount: number;
    currency: string;
  };
  variation_name?: string;
  item_type?: string;
}

interface LineItemDescription {
  order?: {
    line_items?: LineItem[];
  };
}

interface Invoice {
  id: string;
  invoice_id: string;
  square_invoice_number?: string;
  stripe_customer_id: string;
  square_customer_id?: string;
  red_rock_id: string;
  status: string;
  total_amount: number;
  amount_paid: number;
  currency: string;
  payment_status: string;
  hosted_invoice_url: string;
  square_public_url?: string;
  invoice_pdf: string;
  due_date: string;
  period_start: string;
  period_end: string;
  memo: string;
  source: string;
  last_event_id: string;
  is_synced: boolean;
  created: string;
  updated: string;
  line_item_description?: LineItemDescription;
}

/** Stripe: open/sent/overdue · Square: unpaid — paid & other statuses stay hidden on the portal */
const OPEN_INVOICE_STATUSES = ['open', 'sent', 'overdue', 'unpaid'];

// Memoized Search Input Component to prevent focus loss
const SearchInput = React.memo<{
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isSearching: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
}>(({ value, onChange, isSearching, inputRef }) => {
  const searchInputProps = useMemo(() => ({
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon sx={{ color: 'text.secondary' }} />
      </InputAdornment>
    ),
    endAdornment: isSearching ? (
      <InputAdornment position="end">
        <CircularProgress size={20} />
      </InputAdornment>
    ) : null,
  }), [isSearching]);

  return (
    <TextField
      inputRef={inputRef}
      fullWidth
      value={value}
      onChange={onChange}
      placeholder="Type to search (4+ chars for full search)..."
      variant="outlined"
      size="small"
      InputProps={searchInputProps}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
      }}
      autoComplete="off"
    />
  );
});

SearchInput.displayName = 'SearchInput';

// Type guard helper for elevated access levels
const hasElevatedAccess = (accessLevel?: 'normal' | 'all_access' | 'support' | 'admin'): accessLevel is 'all_access' | 'admin' => {
  return accessLevel === 'all_access' || accessLevel === 'admin';
};

// Sidebar component - extracted outside Dashboard to prevent remounting on every render
interface SidebarProps {
  user: any;
  userAssignments: UserAssignment[];
  totalCustomers: number;
  currentAssignment: UserAssignment | null;
  searchInputValue: string;
  handleSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isSearching: boolean;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  displayList: UserAssignment[];
  handleCustomerSelect: (assignment: UserAssignment) => void;
  searchResults: UserAssignment[];
}

const Sidebar = React.memo<SidebarProps>(({
  user,
  userAssignments,
  totalCustomers,
  currentAssignment,
  searchInputValue,
  handleSearchInputChange,
  isSearching,
  searchInputRef,
  displayList,
  handleCustomerSelect,
  searchResults,
}) => (
  <Box sx={{ 
    width: 320, 
    height: '100%', 
    bgcolor: 'grey.50',
    borderRight: '1px solid',
    borderColor: 'grey.200',
    display: 'flex',
    flexDirection: 'column'
  }}>
    {/* Header Section */}
    <Box sx={{ 
      p: 3, 
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
    }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Your Companies
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {user?.email}
      </Typography>
      <Box sx={{ 
        mt: 2, 
        p: 1.5, 
        bgcolor: 'rgba(255,255,255,0.1)', 
        borderRadius: 2,
        backdropFilter: 'blur(10px)'
      }}>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          {(user?.access_level === 'all_access' || user?.access_level === 'admin')
            ? `${userAssignments.length} out of ${totalCustomers} customers loaded, search for more` 
            : `${userAssignments.length} Company${userAssignments.length !== 1 ? 'ies' : ''} Assigned`}
        </Typography>
      </Box>
    </Box>

    {/* Companies List / Search */}
    <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ 
        color: 'text.secondary', 
        mb: 2, 
        px: 1,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5
      }}>
        {(user?.access_level === 'all_access' || user?.access_level === 'admin') ? 'Search Company' : 'Select Company'}
      </Typography>
      
      {(user?.access_level === 'all_access' || user?.access_level === 'admin') ? (
        // Searchable list for all_access users
        <>
          {/* Search Box */}
          <Box sx={{ mb: 2 }}>
            <SearchInput
              value={searchInputValue}
              onChange={handleSearchInputChange}
              isSearching={isSearching}
              inputRef={searchInputRef}
            />
          </Box>
          
          {/* Customer List - Shows filtered results */}
          <List sx={{ p: 0, flex: 1, overflow: 'auto' }}>
            {displayList.map((assignment, index) => (
              <ListItem key={assignment.red_rock_id || assignment.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  selected={currentAssignment?.red_rock_id === assignment.red_rock_id || currentAssignment?.id === assignment.id}
                  onClick={() => handleCustomerSelect(assignment)}
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    transition: 'all 0.2s ease-in-out',
                    border: '1px solid transparent',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderColor: 'grey.300',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderColor: 'primary.dark',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        transform: 'translateY(-1px)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                      '& .MuiChip-root': {
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'primary.contrastText',
                      }
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: currentAssignment?.id === assignment.id ? 'rgba(255,255,255,0.2)' : 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: currentAssignment?.id === assignment.id ? 'primary.contrastText' : 'white'
                    }}>
                      <BusinessIcon />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 600,
                        lineHeight: 1.2,
                        mb: 0.5
                      }}>
                        {assignment.red_rock_full_entity_name}
                      </Typography>
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {currentAssignment?.id === assignment.id && (
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 22,
                              fontWeight: 500,
                              borderColor: 'rgba(255,255,255,0.5)',
                              color: 'primary.contrastText'
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {searchInputValue.trim().length >= 4 && searchResults.length === 0 && !isSearching && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No companies found
                </Typography>
              </Box>
            )}
          </List>
        </>
      ) : (
        // Normal list for regular users
        <List sx={{ p: 0, flex: 1 }}>
          {userAssignments.map((assignment, index) => (
            <ListItem key={assignment.red_rock_id || assignment.id} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={currentAssignment?.red_rock_id === assignment.red_rock_id || currentAssignment?.id === assignment.id}
                onClick={() => handleCustomerSelect(assignment)}
                sx={{
                  borderRadius: 2,
                  p: 2,
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid transparent',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderColor: 'grey.300',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.dark',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'translateY(-1px)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                    '& .MuiChip-root': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'primary.contrastText',
                    }
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: currentAssignment?.id === assignment.id ? 'rgba(255,255,255,0.2)' : 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: currentAssignment?.id === assignment.id ? 'primary.contrastText' : 'white'
                  }}>
                    <BusinessIcon />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600,
                      lineHeight: 1.2,
                      mb: 0.5
                    }}>
                      {assignment.red_rock_full_entity_name}
                    </Typography>
                  }
                  secondaryTypographyProps={{ component: 'span' }}
                  secondary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={assignment.user_access_type === 'can_edit' ? 'Full Access' : 'View Only'} 
                        size="small" 
                        color={assignment.user_access_type === 'can_edit' ? 'success' : 'default'}
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: 22,
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                      {currentAssignment?.id === assignment.id && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem', 
                            height: 22,
                            fontWeight: 500,
                            borderColor: 'rgba(255,255,255,0.5)',
                            color: 'primary.contrastText'
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>

    {/* Footer */}
    <Box sx={{ 
      p: 2, 
      borderTop: '1px solid', 
      borderColor: 'grey.200',
      bgcolor: 'background.paper'
    }}>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
        Client Portal v1.0
      </Typography>
    </Box>
  </Box>
));

Sidebar.displayName = 'Sidebar';

const Dashboard: React.FC = () => {
  const { user, userAssignments, totalCustomers, currentAssignment, setCurrentAssignment, logout, checkTermsAccepted, brand } = useAuth();
  const navigate = useNavigate();

  // Redirect support users to user management
  useEffect(() => {
    if (user && user.access_level === 'support') {
      navigate('/user-management', { replace: true });
    }
  }, [user, navigate]);
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const visibleInvoices = useMemo(
    () =>
      invoices.filter((inv) =>
        OPEN_INVOICE_STATUSES.includes(inv.status.toLowerCase())
      ),
    [invoices]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsPending, setTermsPending] = useState(false);
  // Track which company's terms modal has been dismissed by all_access user
  const dismissedTermsModalRef = useRef<string | null>(null);
  const [folderStatus, setFolderStatus] = useState<ClientFolderStatus | null>(null);
  const [clientFolder, setClientFolder] = useState<ClientFolder | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileRequestUrl, setFileRequestUrl] = useState('');
  const [fileRequestError, setFileRequestError] = useState('');
  const [fileRequestLoading, setFileRequestLoading] = useState(false);
  const [folderFiles, setFolderFiles] = useState<BoxFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showLineItemsModal, setShowLineItemsModal] = useState(false);
  const [engagementLetter, setEngagementLetter] = useState<CustomerEngagementLetter | null>(null);
  const [taxReturnStatus, setTaxReturnStatus] = useState<TaxReturnStatus | null>(null);
  const [showTaxReturnCard, setShowTaxReturnCard] = useState(true);
  const [taxReturnTrackerLoading, setTaxReturnTrackerLoading] = useState(false);
  const [taxReturnTrackerError, setTaxReturnTrackerError] = useState('');
  const [taxReturnTrackerResult, setTaxReturnTrackerResult] = useState<TaxReturnTrackerResult | null>(null);
  const [taxReturnNotes, setTaxReturnNotes] = useState<TaxReturnNote[]>([]);

  const sanitizedTaxReturnHtml = useMemo(() => {
    if (!taxReturnStatus?.text) {
      return '';
    }
    return DOMPurify.sanitize(taxReturnStatus.text, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'],
      ALLOWED_ATTR: [],
    });
  }, [taxReturnStatus]);
  
  // Search state for all_access users
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const [searchResults, setSearchResults] = useState<UserAssignment[]>([]); // API search results
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, [user, currentAssignment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;

    const loadTaxReturnTrackers = async () => {
      if (!currentAssignment?.red_rock_full_entity_name && !currentAssignment?.red_rock_id) {
        setTaxReturnTrackerResult(null);
        setTaxReturnTrackerError('');
        setTaxReturnTrackerLoading(false);
        return;
      }

      setTaxReturnTrackerLoading(true);
      setTaxReturnTrackerError('');
      setTaxReturnTrackerResult(null);

      try {
        const result = await taxReturnTrackerService.fetchTaxReturnTrackers({
          entityName: currentAssignment.red_rock_full_entity_name || '',
          customerRecordId: currentAssignment.red_rock_id,
        });
        if (!cancelled) {
          setTaxReturnTrackerResult(result);
        }
      } catch (error: any) {
        console.error('Failed to load tax return trackers:', error);
        if (!cancelled) {
          setTaxReturnTrackerError(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to load tax return status from Jira'
          );
          setTaxReturnTrackerResult(null);
        }
      } finally {
        if (!cancelled) {
          setTaxReturnTrackerLoading(false);
        }
      }
    };

    const loadTaxReturnNotes = async () => {
      if (!currentAssignment?.red_rock_id) {
        setTaxReturnNotes([]);
        return;
      }
      try {
        const notes = await taxReturnNotesService.fetchTaxReturnNotes(
          currentAssignment.red_rock_id
        );
        if (!cancelled) {
          setTaxReturnNotes(notes);
        }
      } catch (error) {
        console.error('Failed to load tax return notes:', error);
        if (!cancelled) {
          setTaxReturnNotes([]);
        }
      }
    };

    loadTaxReturnTrackers();
    loadTaxReturnNotes();
    return () => {
      cancelled = true;
    };
  }, [currentAssignment?.red_rock_id, currentAssignment?.red_rock_full_entity_name]);

  useEffect(() => {
    if (folderStatus?.status && clientFolder?.box_folder_id) {
      loadFolderFiles(clientFolder.box_folder_id);
    }
  }, [folderStatus, clientFolder]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    // For all_access and admin users, allow loading even without currentAssignment initially
    if (!user?.id) {
      setError('User not found');
      setLoading(false);
      return;
    }
    
    if (!currentAssignment?.red_rock_id) {
      // For all_access and admin users, this is okay - they can search and select
      if (!hasElevatedAccess(user.access_level)) {
      setError('No customer assignment found for this account');
        setLoading(false);
        return;
      }
      // For all_access and admin users, clear folder state and return without error
      setClientFolder(null);
      setFolderFiles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if terms and conditions have been accepted
      const termsAccepted = await checkTermsAccepted(user.id, currentAssignment.red_rock_id);
      
      if (!termsAccepted) {
        // Set pending state for tracking
        setTermsPending(true);
        
        // For all_access and admin users: only show modal if it hasn't been dismissed for this company
        // For normal users: always show modal
        if (user.access_level === 'all_access' || user.access_level === 'admin') {
          // Only show modal if it hasn't been dismissed for this specific company
          if (dismissedTermsModalRef.current !== currentAssignment.red_rock_id) {
            setShowTermsModal(true);
          }
        } else {
          // Normal users: always show modal
          setShowTermsModal(true);
        }
        
        // For all_access and admin users: continue loading data even if terms not accepted
        // For normal users: block data loading until terms are accepted
        if (!hasElevatedAccess(user.access_level)) {
          setLoading(false);
          return;
        }
        // For all_access and admin users, continue to load data below
      } else {
        // Terms are accepted, clear pending state and close snackbar
        setTermsPending(false);
        setSnackbarOpen(false);
      }

      // Load customer data (for all_access and admin users, this runs even if terms not accepted)
      const customerData = await customerService.getCustomer(
        currentAssignment.red_rock_id, 
        'client', 
        user.id
      );
      setCustomer(customerData);

      // Load invoices
      const invoicesData = await invoiceService.getInvoicesByCustomer(
        currentAssignment.red_rock_id, 
        'client', 
        user.id
      );
      setInvoices(invoicesData);

      // Load folder status
      const folderStatusData = await customerService.getClientFolderStatus(
        currentAssignment.red_rock_id,
        'client',
        user.id
      );
      setFolderStatus(folderStatusData);

      // When folder is set up, load client_folders record to get box_folder_id for file listing
      if (folderStatusData?.status) {
        try {
          const folderRecord = await clientFolderService.getClientFolder(currentAssignment.red_rock_id);
          setClientFolder(folderRecord ?? null);
        } catch (err) {
          console.error('Error loading client folder for Box:', err);
          setClientFolder(null);
        }
      } else {
        setClientFolder(null);
        setFolderFiles([]);
      }

      // Load tax return status
      const taxReturnStatusData = await customerService.getTaxReturnStatus(
        currentAssignment.red_rock_id,
        'client',
        user.id
      );
      setTaxReturnStatus(taxReturnStatusData);
      // Reset show card state when tax return status is loaded
      // Only show modal if card_status is true and client hasn't viewed it yet
      if (taxReturnStatusData && taxReturnStatusData.card_status && !taxReturnStatusData.client_viewed) {
        setShowTaxReturnCard(true);
      } else {
        setShowTaxReturnCard(false);
      }

      // Load engagement letter
      const engagementLetterData = await engagementService.getActiveEngagementLetter(
        currentAssignment.red_rock_id
      );
      setEngagementLetter(engagementLetterData);

      // Update Square link status for scheduled invoices (background operation)
      // This runs for both "normal" and "all_access" users when they select a customer
      invoiceService.updateSquareLinkStatus(currentAssignment.red_rock_id)
        .then((result) => {
          if (result.updated > 0) {
            console.log(`✅ Updated ${result.updated} Square invoice(s) for customer ${currentAssignment.red_rock_id}`);
            // Optionally refresh invoices if any were updated
            if (result.updated > 0) {
              // Reload invoices to get updated square_public_url and status
              invoiceService.getInvoicesByCustomer(
                currentAssignment.red_rock_id,
                'client',
                user.id
              ).then((updatedInvoices) => {
                setInvoices(updatedInvoices);
              }).catch((err) => {
                console.error('Error refreshing invoices after Square update:', err);
              });
            }
          } else if (result.total_scheduled === 0) {
            console.log(`ℹ️ No scheduled invoices found for customer ${currentAssignment.red_rock_id}`);
          } else {
            console.log(`⚠️ Failed to update ${result.failed} invoice(s) for customer ${currentAssignment.red_rock_id}`);
          }
        })
        .catch((err) => {
          console.error('Error updating Square link status:', err);
          // Don't show error to user - this is a background operation
        });
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFolderFiles = async (boxFolderId: string) => {
    if (!boxFolderId) return;
    setLoadingFiles(true);
    try {
      const filesResponse = await boxService.getFiles(boxFolderId);
      if (filesResponse.success) {
        setFolderFiles(filesResponse.files);
      } else {
        setFolderFiles([]);
        if (filesResponse.error) {
          console.error('Failed to load files:', filesResponse.error);
        }
      }
    } catch (error) {
      console.error('Error loading folder files:', error);
      setFolderFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handlePayInvoice = (invoice: Invoice) => {
    // Handle Square invoices
    if (invoice.source?.toLowerCase() === 'square' && invoice.square_public_url) {
      window.open(invoice.square_public_url, '_blank');
    }
    // Handle Stripe invoices
    else if (invoice.hosted_invoice_url) {
      window.open(invoice.hosted_invoice_url, '_blank');
    }
  };

  const handleDownloadPDF = (invoice: Invoice) => {
    if (invoice.invoice_pdf) {
      window.open(invoice.invoice_pdf, '_blank');
    }
  };

  const uploadSelectedFiles = async (files: FileList | File[]) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    for (const file of list) {
      await uploadFileToBox(file);
    }
  };

  const handleFolderAccess = async () => {
    if (!currentAssignment?.red_rock_id) {
      setUploadError('No customer assignment found');
      return;
    }

    try {
      setUploadError('');
      setUploadSuccess(false);

      // Create file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
      fileInput.multiple = true;
      
      fileInput.onchange = async (event) => {
        const target = event.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) {
          return;
        }
        await uploadSelectedFiles(target.files);
      };

      fileInput.click();
      
    } catch (error: any) {
      console.error('Error accessing folder:', error);
      setUploadError(error.message || 'Failed to access folder');
    }
  };

  const handleUploadDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };

  const handleUploadDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };

  const handleUploadDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleUploadDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    if (!currentAssignment?.red_rock_id) {
      setUploadError('No customer assignment found');
      return;
    }
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setUploadError('');
    setUploadSuccess(false);
    await uploadSelectedFiles(files);
  };

  const uploadFileToBox = async (file: File) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadError('');
      setUploadSuccess(false);
      setFileRequestError('');

      console.log(`📤 Uploading file to Box: ${file.name}`);

      if (!currentAssignment?.red_rock_id) {
        setUploadError('No customer selected for upload');
        return;
      }

      const result = await boxService.uploadFileChunked(file, currentAssignment.red_rock_id, (progress) => {
        setUploadProgress(progress);
      });

      if (result.success) {
        setUploadSuccess(true);
        console.log('✅ File uploaded successfully');
        
        // Refresh the files list
        // Temporarily disabled: folder file listing fetch
        // await loadFolderFiles();
        
        // Reset success message after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      } else {
        setUploadError(result.error || 'Upload failed');
        console.error('❌ Upload failed:', result.error);
      }

    } catch (error: any) {
      console.error('❌ Upload error:', error);
      setUploadError(error.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateFileRequest = async () => {
    if (!currentAssignment?.red_rock_id) {
      setFileRequestError('No customer selected for upload');
      return;
    }
    try {
      setFileRequestLoading(true);
      setFileRequestError('');
      const title = currentAssignment.red_rock_full_entity_name
        ? `Upload files for ${currentAssignment.red_rock_full_entity_name}`
        : undefined;
      const result = await boxService.createFileRequest(currentAssignment.red_rock_id, {
        title,
        status: 'active',
        isEmailRequired: true,
        isDescriptionRequired: false
      });
      if (result.success && result.url) {
        setFileRequestUrl(result.url);
        window.open(result.url, '_blank', 'noopener,noreferrer');
      } else {
        setFileRequestError(result.error || 'Failed to create upload link');
      }
    } catch (error: any) {
      setFileRequestError(error.message || 'Failed to create upload link');
    } finally {
      setFileRequestLoading(false);
    }
  };

  const handleCustomerSelect = async (assignment: any) => {
    // Close any existing modal when switching customers
    // loadData() will check terms for the new company and set termsPending appropriately
    setShowTermsModal(false);
    setSnackbarOpen(false);
    // Reset dismissed modal tracking when switching to a new company
    dismissedTermsModalRef.current = null;
    // Clear engagement letter state when switching customers
    setEngagementLetter(null);
    // Reset tax return card visibility when switching customers
    setShowTaxReturnCard(true);
    setTaxReturnTrackerResult(null);
    setTaxReturnTrackerError('');
    setTaxReturnNotes([]);
    setDragOver(false);

    let nextAssignment = assignment as UserAssignment;
    // Enrich lead_source when missing (all_access view path has no expand)
    if (
      nextAssignment?.red_rock_id &&
      nextAssignment.lead_source == null &&
      nextAssignment.lead_source_id == null
    ) {
      try {
        const leadFields = await authService.getCustomerLeadSourceFields(nextAssignment.red_rock_id);
        nextAssignment = {
          ...nextAssignment,
          lead_source: leadFields.lead_source,
          lead_source_id: leadFields.lead_source_id,
        };
      } catch (err) {
        console.error('Failed to enrich lead source for branding:', err);
      }
    }

    setCurrentAssignment(nextAssignment);
    setFileRequestUrl('');
    setFileRequestError('');
    // Note: Don't clear termsPending here - let loadData() set it based on new company's terms status
  };

  const handleGoHome = () => {
    setCurrentAssignment(null);
    setCustomer(null);
    setInvoices([]);
    setFolderStatus(null);
    setEngagementLetter(null);
    setTaxReturnStatus(null);
    setShowTaxReturnCard(true);
    setTaxReturnTrackerResult(null);
    setTaxReturnTrackerError('');
    setTaxReturnTrackerLoading(false);
    setTaxReturnNotes([]);
    setError('');
    setFileRequestUrl('');
    setFileRequestError('');
  };

  // Client-side filter function for 1-3 character searches
  const filterCustomersClientSide = useCallback((searchTerm: string) => {
    if (!userAssignments || userAssignments.length === 0) {
      return [];
    }
    
    const term = searchTerm.toLowerCase().trim();
    return userAssignments.filter(assignment => 
      assignment.red_rock_full_entity_name?.toLowerCase().includes(term)
    );
  }, [userAssignments]);

  // API search function for 4+ character searches
  const performSearch = useCallback(async (searchTerm: string, abortSignal?: AbortSignal) => {
    if (!user || !hasElevatedAccess(user.access_level)) {
      return;
    }

    setIsSearching(true);
    try {
      console.log('🔍 Performing API search for:', searchTerm);
      const result = await authService.searchCustomers(searchTerm, 1, 30, abortSignal);
      console.log('🔍 Search results:', result);
      
      // Transform API results to match UserAssignment format
      const transformedResults: UserAssignment[] = result.items.map(item => ({
        id: item.id,
        red_rock_id: item.red_rock_id,
        red_rock_full_entity_name: item.red_rock_full_entity_name,
        user_access_type: item.user_access_type,
        assigned_date: item.assigned_date,
      }));
      
      setSearchResults(transformedResults);
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('🔍 Search request cancelled');
        return;
      }
      console.error('Error searching customers:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [user]);

  // Hybrid search handler: client-side for 1-3 chars, API for 4+ chars
  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value || '';
    const trimmedValue = inputValue.trim();
    
    // Store the active element before state update
    const activeElement = document.activeElement;
    const wasFocused = activeElement === event.target;
    
    setSearchInputValue(inputValue);
    
    // Restore focus if it was lost after state update
    if (wasFocused && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      // Use setTimeout to restore focus after React's render cycle
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position
          const input = searchInputRef.current;
          if (input.setSelectionRange) {
            const cursorPos = inputValue.length;
            input.setSelectionRange(cursorPos, cursorPos);
          }
        }
      }, 0);
    }
    
    // Cancel any pending API request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }
    
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
      searchDebounceTimerRef.current = null;
    }

    // Hybrid approach:
    // 1-3 characters: Client-side filter only (instant, no API call)
    // 4+ characters: API call with debounce
    if (trimmedValue.length === 0) {
      // Clear search results when search is empty
      setSearchResults([]);
    } else if (trimmedValue.length >= 1 && trimmedValue.length <= 3) {
      // Client-side filter for 1-3 characters (instant results)
      // Results will be shown in the filtered userAssignments list
      setSearchResults([]);
    } else if (trimmedValue.length >= 4) {
      // API call for 4+ characters with debounce
      const timer = setTimeout(() => {
        console.log('🔍 Debounce timer fired, calling API search with:', trimmedValue);
        // Create new AbortController for this request
        const abortController = new AbortController();
        searchAbortControllerRef.current = abortController;
        performSearch(trimmedValue, abortController.signal);
        searchDebounceTimerRef.current = null;
      }, 500); // 500ms debounce for API calls
      searchDebounceTimerRef.current = timer;
    }
  }, [performSearch]);


  // Memoize the display list to prevent unnecessary re-renders
  const displayList = useMemo(() => {
    if (searchInputValue.trim().length === 0) {
      // No search - show all loaded customers
      return userAssignments;
    } else if (searchInputValue.trim().length >= 1 && searchInputValue.trim().length <= 3) {
      // 1-3 chars: Client-side filter
      return filterCustomersClientSide(searchInputValue);
    } else if (searchInputValue.trim().length >= 4) {
      // 4+ chars: Show API search results
      return searchResults;
    }
    return userAssignments;
  }, [searchInputValue, userAssignments, searchResults, filterCustomersClientSide]);

  // Cleanup timer and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleTermsAccepted = async () => {
    // Clear pending state when terms are accepted
    setTermsPending(false);
    setShowTermsModal(false);
    setSnackbarOpen(false); // Close any open snackbar
    // Add a delay to ensure database has committed the transaction
    // PocketBase might need a moment to index/commit the relation field
    setTimeout(() => {
    loadData();
    }, 1000); // Increased to 1 second
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const isElevatedUser = hasElevatedAccess(user?.access_level);

  const handleTermsModalClose = () => {
    // For all_access and admin users, allow closing with warning
    if (user?.access_level === 'all_access' || user?.access_level === 'admin') {
      setShowTermsModal(false);
      // Mark this company's modal as dismissed to prevent reopening
      if (currentAssignment?.red_rock_id) {
        dismissedTermsModalRef.current = currentAssignment.red_rock_id;
      }
      // Show warning snackbar
      setSnackbarOpen(true);
      // Terms remain pending (termsPending stays true)
      // Note: Don't call loadData() here - it would create a loop by rechecking terms
      // Data is already being loaded in parallel when loadData() runs (even if terms not accepted)
    }
    // For normal users, this function should not be called (modal blocks)
    // This ensures compliance for normal users
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      // Stripe statuses
      case 'paid':
        return 'success';
      case 'open':
        return 'warning';
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'overdue':
        return 'error';
      case 'cancelled':
      case 'canceled':
        return 'error';
      case 'partially_paid':
        return 'warning';
      case 'void':
        return 'error';
      // Square statuses
      case 'unpaid':
        return 'warning';
      case 'scheduled':
        return 'info';
      case 'partially_refunded':
        return 'warning';
      case 'refunded':
        return 'default';
      case 'failed':
        return 'error';
      case 'payment_pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD', source?: string) => {
    // Square amounts are already in dollars (converted in webhook)
    // Stripe amounts are in cents (need to convert)
    const amountInDollars = source?.toLowerCase() === 'square' ? amount : amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amountInDollars);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency from cents (Square line items use cents)
  const formatCurrencyFromCents = (amount: number, currency: string = 'USD') => {
    const amountInDollars = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amountInDollars);
  };

  // Handle invoice click to show line items
  const handleInvoiceClick = (invoice: Invoice) => {
    if (invoice.line_item_description?.order?.line_items && invoice.line_item_description.order.line_items.length > 0) {
      setSelectedInvoice(invoice);
      setShowLineItemsModal(true);
    }
  };

  // Close line items modal
  const handleCloseLineItemsModal = () => {
    setShowLineItemsModal(false);
    setSelectedInvoice(null);
  };

  // Handle tax return status modal close and mark as viewed
  const handleTaxReturnStatusClose = async () => {
    if (taxReturnStatus && taxReturnStatus.id && !taxReturnStatus.client_viewed) {
      try {
        await customerService.updateTaxReturnStatusViewed(taxReturnStatus.id);
        // Update local state to reflect the change
        setTaxReturnStatus({
          ...taxReturnStatus,
          client_viewed: true
        });
      } catch (error) {
        console.error('Error updating tax return status viewed:', error);
        // Still close the modal even if update fails
      }
    }
    setShowTaxReturnCard(false);
  };

  // Get line items from invoice
  const getLineItems = (invoice: Invoice): LineItem[] => {
    return invoice.line_item_description?.order?.line_items || [];
  };

  // TODO: Use openInvoices and totalOutstanding when displaying outstanding balance
  // const openInvoices = invoices.filter(invoice => 
  //   ['open', 'sent', 'overdue', 'unpaid'].includes(invoice.status?.toLowerCase() || '')
  // );
  // const totalOutstanding = openInvoices.reduce((sum, invoice) => {
  //   // Convert to dollars for calculation (Square is already in dollars, Stripe is in cents)
  //   const totalAmount = invoice.source?.toLowerCase() === 'square' 
  //     ? invoice.total_amount 
  //     : invoice.total_amount / 100;
  //   const paidAmount = invoice.source?.toLowerCase() === 'square'
  //     ? invoice.amount_paid
  //     : invoice.amount_paid / 100;
  //   return sum + (totalAmount - paidAmount);
  // }, 0);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: 320,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            border: 'none',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Sidebar
          user={user}
          userAssignments={userAssignments}
          totalCustomers={totalCustomers}
          currentAssignment={currentAssignment}
          searchInputValue={searchInputValue}
          handleSearchInputChange={handleSearchInputChange}
          isSearching={isSearching}
          searchInputRef={searchInputRef}
          displayList={displayList}
          handleCustomerSelect={handleCustomerSelect}
          searchResults={searchResults}
        />
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {/* Red Indicator Badge for Pending T&C (all_access and admin users only) */}
        {(user?.access_level === 'all_access' || user?.access_level === 'admin') && termsPending && currentAssignment && (
          <Box
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              zIndex: 1300, // Above most content but below modals
              cursor: 'pointer',
              maxWidth: 'calc(100vw - 32px)', // Ensure it doesn't overflow viewport
            }}
            onClick={() => setShowTermsModal(true)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'error.main',
                color: 'white',
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
                whiteSpace: 'nowrap', // Prevent text wrapping
                '&:hover': {
                  bgcolor: 'error.dark',
                  boxShadow: '0 6px 16px rgba(211, 47, 47, 0.5)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <GavelIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                T&C Pending
              </Typography>
            </Box>
          </Box>
        )}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 4,
            }}
          >
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {customer ? (customer.entity_type?.toLowerCase() === 'individual' ? customer.red_rock_full_entity_name : customer.company_name) : 'Select a Company'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {currentAssignment ? `Company: ${currentAssignment.red_rock_full_entity_name}` : 'Choose a company from the sidebar'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              {/* Brand logo (Red Rock or The Accounting Lab) - Top Right */}
              <Box
                component="img"
                src={getBrandLogoSrc(brand)}
                alt={getBrandLogoAlt(brand)}
                sx={{
                  height: brand === 'tal' ? { xs: 48, sm: 56 } : { xs: 28, sm: 32 },
                  width: 'auto',
                  maxWidth: brand === 'tal' ? { xs: 160, sm: 200 } : { xs: 120, sm: 140 },
                  objectFit: 'contain',
                  flexShrink: 0,
                  opacity: 0.95,
                  '&:hover': {
                    opacity: 1,
                  },
                  transition: 'opacity 0.2s ease-in-out',
                }}
              />
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {(user?.access_level === 'all_access' || user?.access_level === 'admin') && (
                  <Button
                    variant="outlined"
                    startIcon={<HomeIcon />}
                    onClick={handleGoHome}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Button>
                )}
                {(user?.access_level === 'all_access' || user?.access_level === 'admin') && (
                  <Button
                    component={Link}
                    to="/user-management"
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    size="small"
                  >
                    User Management
                  </Button>
                )}
                <Tooltip title="Refresh Data">
                  <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  onClick={() => {
                    logout();
                    navigate(getLoginPath(), { replace: true });
                  }}
                  size="small"
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!currentAssignment ? (
            isElevatedUser ? (
              <Paper
                sx={{
                  p: 5,
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  background: 'linear-gradient(135deg, #f6f9ff 0%, #ffffff 100%)',
                  boxShadow: '0 8px 24px rgba(25, 118, 210, 0.08)',
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 3,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 8px 20px rgba(25, 118, 210, 0.3)',
                    }}
                  >
                    <BusinessIcon sx={{ color: 'white', fontSize: 36 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
                    Welcome to the Client Portal
                  </Typography>
                  <Typography color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
                    Start by selecting a company from the left. You can search the full list and open any company dashboard when ready.
                  </Typography>
                </Box>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <SearchIcon color="primary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Search Companies
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Use the search box to find any company across the full customer list.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <ReceiptIcon color="secondary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            View Invoices
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Open a company to review invoices, payments, and engagement status.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <PersonAddIcon color="primary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Manage Access
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Use User Management to create or update client portal users.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${userAssignments.length} loaded of ${totalCustomers} total`}
                    color="primary"
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={() => searchInputRef.current?.focus()}
                  >
                    Find a Company
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Select a Company
                </Typography>
                <Typography color="text.secondary">
                  Choose a company from the sidebar to view invoices and details
                </Typography>
              </Paper>
            )
          ) : (
            <>
              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <BusinessIcon color="primary" sx={{ mr: 2 }} />
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Company
                          </Typography>
                          <Typography variant="h6">
                            {customer ? (customer.entity_type?.toLowerCase() === 'individual' ? customer.red_rock_full_entity_name : customer.company_name) : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <ReceiptIcon color="secondary" sx={{ mr: 2 }} />
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Open Invoices
                          </Typography>
                          <Typography variant="h6">
                            {visibleInvoices.length}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>

              <TaxReturnStatusTracker
                loading={taxReturnTrackerLoading}
                error={taxReturnTrackerError}
                result={taxReturnTrackerResult}
                notes={taxReturnNotes}
              />

              {/* Company Details */}
              {customer && (
                <Paper sx={{ p: 3, mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Company Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Company Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {customer.entity_type?.toLowerCase() === 'individual' ? customer.red_rock_full_entity_name : customer.company_name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {customer.invoice_email || customer.email}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Phone
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {customer.primary_phone || customer.phone || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Status
                      </Typography>
                      <Chip
                        label={customer.status}
                        color={customer.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {customer.address && `${customer.address}, ${customer.city}, ${customer.state} ${customer.zip}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Engagement Letter Widget - Only show when active entry exists */}
              {engagementLetter && engagementLetter.active && (
                <Paper 
                  sx={{ 
                    p: 3, 
                    mb: 4, 
                    bgcolor: 'primary.50', 
                    border: '1px solid', 
                    borderColor: 'primary.200',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.15)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        }}
                      >
                        <DescriptionIcon sx={{ color: 'white', fontSize: 32 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" color="primary.dark" gutterBottom sx={{ fontWeight: 600 }}>
                          Engagement Letter Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Your engagement letter is ready for review. Click below to view and download.
                        </Typography>
                        <Chip
                          label="Active"
                          color="success"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 24,
                          }}
                        />
                      </Box>
                    </Box>
                    {engagementLetter.engagement_letter_link && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<LaunchIcon />}
                        endIcon={<DescriptionIcon />}
                        onClick={() => window.open(engagementLetter.engagement_letter_link, '_blank')}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease-in-out',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        View Letter
                      </Button>
                    )}
                  </Box>
                </Paper>
              )}


              {/* Tax Return Status Banner - Shows after client has viewed the modal */}
              {taxReturnStatus && taxReturnStatus.card_status && taxReturnStatus.client_viewed && (
                <Paper 
                  sx={{ 
                    p: 3, 
                    mb: 4, 
                    bgcolor: 'background.paper',
                    border: '1px solid', 
                    borderColor: 'success.200',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.15)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      }}
                    >
                      <CheckCircleIcon sx={{ color: 'white', fontSize: 26 }} />
                    </Box>
                    <Typography variant="h6" color="success.dark" sx={{ fontWeight: 600 }}>
                      Tax Return Update
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    <Typography component="div" variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {taxReturnStatus?.text ? (
                        <Box
                          component="div"
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            '& p': { margin: '0 0 6px 0', textAlign: 'left' },
                            '& p:last-of-type': { marginBottom: 0 },
                            '& ul, & ol': { margin: 0, paddingLeft: '1.25em', textAlign: 'left' },
                            '& li': { margin: 0, paddingLeft: 0, textAlign: 'left' },
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizedTaxReturnHtml }}
                        />
                      ) : (
                        'Your tax return status has been updated!'
                      )}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* Folder Status */}
              {folderStatus && folderStatus.status && (
                <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                      <FolderOpenIcon sx={{ color: 'success.main', mr: 2, fontSize: 32, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" color="success.dark" gutterBottom sx={{ mb: 0.5 }}>
                          📁 Folder Ready
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Your documents are ready for review. Drag files below or click Upload Files.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <FolderOpenIcon />}
                      disabled={uploading}
                      onClick={handleFolderAccess}
                      sx={{
                        px: 3,
                        py: 1.25,
                        height: 48,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Files'}
                    </Button>
                  </Box>

                  <Box
                    onDragEnter={handleUploadDragEnter}
                    onDragOver={handleUploadDragOver}
                    onDragLeave={handleUploadDragLeave}
                    onDrop={handleUploadDrop}
                    onClick={() => {
                      if (!uploading) handleFolderAccess();
                    }}
                    sx={{
                      border: '2px dashed',
                      borderColor: dragOver
                        ? 'success.main'
                        : 'rgba(76, 175, 80, 0.45)',
                      bgcolor: dragOver
                        ? 'rgba(76, 175, 80, 0.12)'
                        : 'rgba(76, 175, 80, 0.04)',
                      borderRadius: 2,
                      p: 2,
                      mb: 1.5,
                      minHeight: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      cursor: uploading ? 'default' : 'pointer',
                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    }}
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 32,
                        color: dragOver ? 'success.dark' : 'success.main',
                        mb: 0.75,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      {uploading
                        ? `Uploading… ${uploadProgress}%`
                        : dragOver
                          ? 'Drop files to upload'
                          : 'Drag & drop files here'}
                    </Typography>
                    {!uploading && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        or click to browse · PDF, Office, images, text
                      </Typography>
                    )}
                  </Box>

                  <Alert
                    severity="info"
                    sx={{
                      mb: 1,
                      alignItems: 'center',
                      '& .MuiAlert-message': {
                        flex: 1,
                        minWidth: 0,
                        py: 0.5,
                      },
                      '& .MuiAlert-action': {
                        pt: 0,
                        pr: 0.5,
                        ml: 1,
                        alignItems: 'center',
                      },
                    }}
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={fileRequestLoading ? <CircularProgress size={14} color="inherit" /> : <LaunchIcon />}
                        onClick={handleCreateFileRequest}
                        disabled={fileRequestLoading || !currentAssignment?.red_rock_id}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          height: 36,
                          px: 1.75,
                        }}
                      >
                        {fileRequestLoading ? 'Creating...' : 'Get upload link'}
                      </Button>
                    }
                  >
                    If upload is failing or the file is very large, use a direct Box upload link.
                  </Alert>
                  
                  {/* Upload Progress */}
                  {uploading && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          Upload Progress:
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                          {uploadProgress}%
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${uploadProgress}%`,
                            height: 8,
                            bgcolor: 'success.main',
                            transition: 'width 0.3s ease-in-out',
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Upload Success Message */}
                  {uploadSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      ✅ File uploaded successfully to your secure folder!
                    </Alert>
                  )}

                  {/* Upload Error Message */}
                  {uploadError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      ❌ {uploadError}
                    </Alert>
                  )}

                  {fileRequestError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      ❌ {fileRequestError}
                    </Alert>
                  )}

                  {fileRequestUrl && (
                    <Alert
                      severity="success"
                      sx={{ mt: 2 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<LaunchIcon />}
                          href={fileRequestUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open upload page
                        </Button>
                      }
                    >
                      Upload link created. You can share or open it now.
                    </Alert>
                  )}

                  {/* Files in Your Folder */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <FolderOpenIcon sx={{ mr: 1 }} />
                      Files in Your Folder
                    </Typography>
                    
                    {loadingFiles ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading files...
                        </Typography>
                      </Box>
                    ) : folderFiles.length > 0 ? (
                      <Box
                        sx={{
                          maxHeight: 400,
                          overflowY: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <List disablePadding>
                          {folderFiles.map((file, index) => (
                            <ListItem key={file.id || index} divider={index < folderFiles.length - 1}>
                              <ListItemIcon>
                                <ReceiptIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={file.name}
                                secondary={
                                  <>
                                    <Typography variant="caption" display="block">
                                      Size: {file.fileSizeInKB} KB
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Uploaded: {new Date(file.creationDate).toLocaleDateString()}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ) : (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No files uploaded yet. Use the "Upload Files" button above to add documents.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                </Grid>
              )}

              {/* Open Invoices — overdue / unpaid only */}
              <Grid size={{ xs: 12, md: folderStatus?.status ? 6 : 12 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  Open Invoices
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {visibleInvoices.length === 0 ? (
                  <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No open invoices
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: 700,
                      overflowY: 'auto',
                      pr: 0.5,
                    }}
                  >
                  <Grid container spacing={2}>
                    {visibleInvoices.map((invoice) => {
                        // Debug logging for Square invoices (remove in production)
                        if (invoice.source?.toLowerCase() === 'square') {
                          console.log('🔍 Square Invoice Debug:', {
                            id: invoice.id,
                            invoice_id: invoice.invoice_id,
                            source: invoice.source,
                            status: invoice.status,
                            square_public_url: invoice.square_public_url,
                            hasPaymentUrl: !!invoice.square_public_url,
                            showButton: invoice.source?.toLowerCase() === 'square' && 
                                       invoice.status?.toLowerCase() === 'unpaid' && 
                                       invoice.square_public_url && 
                                       typeof invoice.square_public_url === 'string' &&
                                       invoice.square_public_url.trim() !== ''
                          });
                        }
                        const hasLineItems = invoice.line_item_description?.order?.line_items && invoice.line_item_description.order.line_items.length > 0;
                        
                        return (
                      <Grid size={{ xs: 12 }} key={invoice.id}>
                        <Card 
                          variant="outlined"
                          onClick={() => hasLineItems && handleInvoiceClick(invoice)}
                          sx={{
                            borderRadius: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            cursor: hasLineItems ? 'pointer' : 'default',
                            '&:hover': {
                              boxShadow: hasLineItems ? '0 4px 16px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transform: hasLineItems ? 'translateY(-2px)' : 'none',
                            },
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                gap: 3,
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                  <ReceiptIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: 500,
                                      color: 'text.primary',
                                    }}
                                  >
                                    Invoice #{invoice.square_invoice_number || invoice.invoice_id}
                                  </Typography>
                                  {hasLineItems && (
                                    <Tooltip title="Click to view line items">
                                      <ShoppingCartIcon sx={{ color: 'primary.main', fontSize: 18, ml: 1 }} />
                                    </Tooltip>
                                  )}
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                                  <Chip
                                    label={invoice.status.toUpperCase()}
                                    color={getStatusColor(invoice.status) as any}
                                    size="small"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      height: 24,
                                      borderRadius: 2,
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                    Due: {formatDate(invoice.due_date)}
                                  </Typography>
                                </Box>
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MoneyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {formatCurrency(invoice.total_amount, invoice.currency, invoice.source)}
                                </Typography>
                                  </Box>
                                {invoice.amount_paid > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 400 }}>
                                    Paid: {formatCurrency(invoice.amount_paid, invoice.currency, invoice.source)}
                                  </Typography>
                                    </Box>
                                )}
                                </Box>
                              </Box>
                              
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  gap: 1.5,
                                  alignItems: { xs: 'stretch', sm: 'flex-end' },
                                  minWidth: { xs: '100%', sm: 'auto' },
                                }}
                              >
                                {invoice.invoice_pdf && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ReceiptIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadPDF(invoice);
                                    }}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      px: 2.5,
                                      py: 1,
                                      borderWidth: 2,
                                      '&:hover': {
                                        borderWidth: 2,
                                      },
                                    }}
                                  >
                                    Download PDF
                                  </Button>
                                )}
                                {/* Payment button for Stripe invoices */}
                                {invoice.source === 'stripe' && (invoice.status === 'open' || invoice.status === 'sent' || invoice.status === 'overdue') && invoice.hosted_invoice_url && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PaymentIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePayInvoice(invoice);
                                    }}
                                    sx={{
                                      minWidth: 140,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      px: 3,
                                      py: 1.25,
                                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                      '&:hover': {
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                      },
                                    }}
                                  >
                                    {invoice.status === 'overdue' ? 'Pay Overdue' : 'Pay Now'}
                                  </Button>
                                )}
                                {/* Payment button for Square invoices - show for unpaid status */}
                                {invoice.source?.toLowerCase() === 'square' && 
                                 invoice.status?.toLowerCase() === 'unpaid' && 
                                 invoice.square_public_url && 
                                 typeof invoice.square_public_url === 'string' &&
                                 invoice.square_public_url.trim() !== '' && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PaymentIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePayInvoice(invoice);
                                    }}
                                    sx={{
                                      minWidth: 140,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      px: 3,
                                      py: 1.25,
                                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                      '&:hover': {
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                      },
                                    }}
                                  >
                                    Pay Now
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                  </Box>
                )}
              </Paper>
              </Grid>
              </Grid>
            </>
          )}
        </Container>
      </Box>

      {/* Line Items Modal */}
      <Dialog
        open={showLineItemsModal}
        onClose={handleCloseLineItemsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ShoppingCartIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Invoice Line Items
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseLineItemsModal}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {selectedInvoice && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Invoice #{selectedInvoice.square_invoice_number || selectedInvoice.invoice_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total: {formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency, selectedInvoice.source)}
                </Typography>
              </Box>
              
              {getLineItems(selectedInvoice).length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Item</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Quantity</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Unit Price</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getLineItems(selectedInvoice).map((item, index) => {
                        const quantity = parseFloat(item.quantity || '1');
                        const totalPrice = item.total_money?.amount || 0;
                        const unitPrice = item.base_price_money?.amount || (quantity > 0 ? totalPrice / quantity : 0);
                        const currency = item.base_price_money?.currency || item.total_money?.currency || selectedInvoice.currency || 'USD';
                        
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {item.name || 'Unnamed Item'}
                                </Typography>
                                {item.variation_name && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {item.variation_name}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1">
                                {item.quantity || '1'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1">
                                {formatCurrencyFromCents(unitPrice, currency)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatCurrencyFromCents(totalPrice, currency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No line items available
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={handleCloseLineItemsModal}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms and Conditions Modal */}
      {user && currentAssignment && (
        <TermsConditionsModal
          open={showTermsModal}
          onClose={handleTermsModalClose}
          clientId={user.id}
          redRockId={currentAssignment.red_rock_id}
          onTermsAccepted={handleTermsAccepted}
          allowClose={user.access_level === 'all_access' || user.access_level === 'admin'}
          userAccessLevel={user.access_level as UserAccessLevel | undefined}
        />
      )}

      {/* Snackbar for warning when all_access user closes modal */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="warning" 
          sx={{ width: '100%' }}
        >
          Terms & Conditions not accepted. Customer must accept to proceed. 
          Use the red indicator to reopen the modal when the customer is ready to accept.
        </Alert>
      </Snackbar>

      {/* Tax Return Status Modal - Banking Style Notification - Only shows if client hasn't viewed yet */}
      <Dialog
        open={taxReturnStatus && taxReturnStatus.card_status && !taxReturnStatus.client_viewed && showTaxReturnCard ? true : false}
        onClose={handleTaxReturnStatusClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'success.main',
            color: 'white',
            py: 3,
            px: 4,
            position: 'relative',
            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.02em' }}>
                Tax Return Update
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 400 }}>
                Important notification
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleTaxReturnStatusClose}
            size="small"
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              width: 40,
              height: 40,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 5,
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(76, 175, 80, 0.2)',
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.75rem',
                  lineHeight: 1.4,
                }}
              >
                Tax Return Update
              </Typography>
            </Box>
            <Box
              sx={{
                p: 3,
                bgcolor: 'grey.50',
                borderRadius: 3,
                width: '100%',
                border: '1px solid',
                borderColor: 'grey.200',
                textAlign: 'left',
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              <Typography component="div" variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {taxReturnStatus?.text ? (
                  <Box
                    component="div"
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      '& p': { margin: '0 0 6px 0', textAlign: 'left' },
                      '& p:last-of-type': { marginBottom: 0 },
                      '& ul, & ol': { margin: 0, paddingLeft: '1.25em', textAlign: 'left' },
                      '& li': { margin: 0, paddingLeft: 0, textAlign: 'left' },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizedTaxReturnHtml }}
                  />
                ) : (
                  'Your tax return status has been updated!'
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 1,
                p: 3,
                bgcolor: 'grey.50',
                borderRadius: 3,
                width: '100%',
                border: '1px solid',
                borderColor: 'grey.200',
                textAlign: 'left',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    minWidth: 24,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'info.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>
                    i
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: '0.9375rem',
                    color: 'text.secondary',
                  }}
                >
                  This notification contains important information about your tax return.
                  Please review the details and contact us if you have any questions.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            bgcolor: 'grey.50',
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: 'center',
          }}
        >
          <Button
            onClick={handleTaxReturnStatusClose}
            variant="contained"
            color="success"
            size="large"
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              letterSpacing: '0.01em',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Got it, thanks!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
